import { useMemo } from 'react';
import type { UppercaseFirstLetterKeys } from 'shared/src/types';
import type { CustomerNomenclature } from 'shared/src/types';

export interface NomenclatureProps {
  nomenclatureId: keyof UppercaseFirstLetterKeys<CustomerNomenclature>;
}

export interface NomenclatureBaseProps {
  customer?: CustomerNomenclature;
}

export function createTranslator(customer?: CustomerNomenclature) {
  const lexicon = getLexicon(customer);
  return function translate(term: keyof UppercaseFirstLetterKeys<CustomerNomenclature>) {
    return lexicon[term];
  };
}

export function getLexicon(customer?: CustomerNomenclature): UppercaseFirstLetterKeys<CustomerNomenclature> {
  return {
    VotingLocationDisplayName: customer?.votingLocationDisplayName || 'Voting Location',
    PrecinctDisplayName: customer?.precinctDisplayName || 'Precinct',
    EmployeeDisplayName: customer?.employeeDisplayName || 'Employee',
    TaskDisplayName: customer?.taskDisplayName || 'Task',
    ProjectDisplayName: customer?.projectDisplayName || 'Project',
    InventoryDisplayName: customer?.inventoryDisplayName || 'Inventory',
    DeploymentWorkerDisplayName: customer?.deploymentWorkerDisplayName || 'Deployment Worker',
    PollWorkerDisplayName: customer?.pollWorkerDisplayName || 'PollWorker',
    VoterDisplayName: customer?.voterDisplayName || 'Voter',
    LnaDisplayName: customer?.lnaDisplayName || 'L & A',
    LnaTestDisplayName: customer?.lnaTestDisplayName || 'L & A Test',
    EfProjectContactDisplayName: customer?.efProjectContactDisplayName || 'Ef Project Contact',
    OfficerDisplayName: customer?.officerDisplayName || 'Officer',
    FilingClerkDisplayName: customer?.filingClerkDisplayName || 'Filing Clerk',
    CampaignFinanceDisplayName: customer?.campaignFinanceDisplayName || 'Campaign Finance',
    AbsenteeByMailDisplayName: customer?.absenteeByMailDisplayName || 'Absentee By Mail',
    EarlyVotingDisplayName: customer?.earlyVotingDisplayName || 'Early Voting',
    PwTrainingDisplayName: customer?.pwTrainingDisplayName || 'Poll Worker Training',
    PwSkillsDisplayName: customer?.pwSkillsDisplayName || 'Poll Worker Skills',
    PwRolesDisplayName: customer?.pwRolesDisplayName || 'Poll Worker Roles',
    ThirdPartyDisplayName: customer?.thirdPartyDisplayName || 'Third Party',
    PollworkerElectionDayDisplayName: customer?.pollworkerElectionDayDisplayName || 'Poll Worker Election Day',
    PollworkerStandbyDisplayName: customer?.pollworkerStandbyDisplayName || 'Poll Worker Standby',
    PollworkerTraineeDisplayName: customer?.pollworkerTraineeDisplayName || 'Poll Worker Trainee',
    PollworkerProspectDisplayName: customer?.pollworkerProspectDisplayName || 'Poll Worker Prospect',
    PollworkerApplicantDisplayName: customer?.pollworkerApplicantDisplayName || 'Poll Worker Applicant',
    PollworkerEmployeeIdDisplayName: customer?.pollworkerEmployeeIdDisplayName || 'Poll Worker Employee ID',
    PollworkerOnlineSystemDisplayName: customer?.pollworkerOnlineSystemDisplayName || 'Poll Worker Online System',
    DistrictComboNumberDisplayName: customer?.districtComboNumberDisplayName || 'District Combo Number',
    CardStyleDisplayName: customer?.cardStyleDisplayName || 'Card Style',
    EquipGroupDisplayName: customer?.equipGroupDisplayName || 'Equipment Group',
    PrecinctCodeDisplayName: customer?.precinctCodeDisplayName || 'Precinct Code',
    PrecinctManagerDisplayName: customer?.precinctManagerDisplayName || 'Precinct Manager',
    VoterRegNumberDisplayName: customer?.voterRegNumberDisplayName || 'Voter Reg Number',
    BallotAcceptedDisplayName: customer?.ballotAcceptedDisplayName || 'Ballot Accepted',
    FilerTypeDisplayName: customer?.filerTypeDisplayName || 'Filer Type',
    CandidateDisplayName: customer?.candidateDisplayName || 'Candidate',
    CfVoterRegNoDisplayName: customer?.cfVoterRegNoDisplayName || 'CF Voter Reg No',
    CommitteeDisplayName: customer?.committeeDisplayName || 'Committee',
    ChairPersonDisplayName: customer?.chairPersonDisplayName || 'Chair Person',
    CommitteeExpirationDateDisplayName: customer?.committeeExpirationDateDisplayName || 'Committee Expiration Date',
    OfficeTypeDisplayName: customer?.officeTypeDisplayName || 'Office Type',
    FilerIdDisplayName: customer?.filerIdDisplayName || 'Filer ID',
    PinDisplayName: customer?.pinDisplayName || 'PIN',
    FirstElectionYearDisplayName: customer?.firstElectionYearDisplayName || 'First Election Year',
    CfDateApprovedDisplayName: customer?.cfDateApprovedDisplayName || 'CF Date Approved',
  };
}

export function NomenclatureBase({ nomenclatureId, customer }: NomenclatureProps & NomenclatureBaseProps) {
  const customerLexicon = useMemo(() => getLexicon(customer), [customer]);
  return customerLexicon[nomenclatureId];
}
